// Import necessary hooks and functions from react-redux
import { useSelector, useDispatch } from 'react-redux';
import { useEffect, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
// Import your action creators
import { fetchAmendmentData, updateAmendmentData, saveAmendmentData, setStep } from '../../redux/actions';

function AmendDataValidation({ formId, selectedIndex, step }) {
  const dispatch = useDispatch();
  // Use useSelector to access the current amendment data from the Redux store
  // Adjust these selectors according to your state shape
  const amendmentData = useSelector((state) => {
    const amendments = state?.amendment?.amendExtend?.contracts?.[selectedIndex]?.amendments;
    const lastAmendment = amendments?.[amendments.length - 1];
    // Check if the last amendment has a nested amendmentData structure
    return lastAmendment?.amendmentData ? lastAmendment.amendmentData : lastAmendment;
  });
  const [isEditMode, setIsEditMode] = useState(false);

  const formData = useSelector(state => state?.amendment?.amendExtend?.contracts?.[selectedIndex]);  

  // State hooks for form fields (assuming they are part of amendmentData)
  const completedAt = formData?.packetDetails?.completedAt;
  const dateOnly = new Date(completedAt).toISOString().split('T')[0];
  const [lastContractDate, setLastContractDate] = useState(dateOnly);
  const [buyer1FirstName, setBuyer1FirstName] = useState(amendmentData ? amendmentData.buyer1FirstName : formData?.data?.firstName);
  const [buyer1LastName, setBuyer1LastName] = useState(amendmentData ? amendmentData.buyer1LastName : formData?.data?.lastName);
  const [buyer1Email, setBuyer1Email] = useState(formData?.data?.buyer1Email);
  const [buyer2FirstName, setBuyer2FirstName] = useState(amendmentData ? amendmentData.buyer2FirstName : formData?.data?.tenants?.[0]?.tenantFirstName);
  const [buyer2LastName, setBuyer2LastName] = useState(amendmentData ? amendmentData.buyer2LastName : formData?.data?.tenants?.[0]?.tenantLastName);
  const [buyer2Email, setBuyer2Email] = useState(formData?.data?.tenants?.[0]?.tenantEmail);
  const [seller1Name, setSeller1Name] = useState(amendmentData ? amendmentData.seller1Name : formData?.data?.seller1Name); 
  const [seller2Name, setSeller2Name] = useState(amendmentData ? amendmentData.seller2Name : formData?.data?.seller2Name); 
  const [listingAgentName, setListingAgentName] = useState(amendmentData ? amendmentData.listingAgentName : formData?.data?.listingAgentName); 
  const [listingAgentEmail, setListingAgentEmail] = useState(amendmentData ? amendmentData.listingAgentEmail : formData?.data?.listingAgentEmail); 
  const [amendmentId, setAmendmentId] = useState(
    amendmentData && amendmentData.amendmentId
      ? amendmentData.amendmentId
      : uuidv4() // Generate a new UUID if amendmentId doesn't exist
  );
  const [contractCompletionDate, setContractCompletionDate] = useState(amendmentData ? amendmentData.contractCompletionDate : lastContractDate);
  const [amendmentDate, setAmendmentDate] = useState(new Date());
  const [expirationDate, setExpirationDate] = useState(amendmentData ? amendmentData.expirationDate : '');  
  const [currentDates, setCurrentDates] = useState(Object);
  // const [newDates, setNewDates] = useState(amendmentData ? amendmentData.newDates : {});
  const [newDates, setNewDates] = useState({});
  const [otherDatesText, setOtherDatesText] = useState(amendmentData ? amendmentData.otherDatesText : '');
  const [otherAmendmentsText, setOtherAmendmentsText] = useState(amendmentData ? amendmentData.otherAmendmentsText : '');
  const [sendToSeller, setSendToSeller] = useState('yes');
  const [packetDetails, setPacketDetails] = useState({});
  const [propertyDescription, setPropertyDescription] = useState(formData?.propertyData?.data?.lotInfo?.legalDescription || formData?.data?.propertyDescription);
  const [address, setAddress] = useState(formData?.data?.address);
  const [county, setCounty] = useState(
    amendmentData?.county ||
    formData?.propertyData?.data?.ownerInfo?.mailAddress?.county ||
    formData?.data?.propertyCounty
  );
  const [selectedContract, setSelectedContract] = useState(selectedIndex); 

  const handleEditClick = () => {
    setIsEditMode(!isEditMode);
  };

  const handleCancel = () => {
    setIsEditMode(false);
  };

  // Handler for input changes
  const handleInputChange = (field, value) => {
    switch (field) {
          case 'buyer1FirstName':
            setBuyer1FirstName(value);
            break;
          case 'buyer1LastName':
            setBuyer1LastName(value);
            break;
          case 'buyer1Email':
            setBuyer1Email(value);
          case 'buyer2FirstName':
            setBuyer2FirstName(value);
            break;
          case 'buyer2LastName':
            setBuyer2LastName(value);
            break;
          case 'buyer2Email':
            setBuyer2Email(value);
          case 'seller1Name':
            setSeller1Name(value);
            break;
          case 'seller2Name':
            setSeller2Name(value);
            break;
          case 'listingAgentName':
            setListingAgentName(value);
            break;
          case 'listingAgentEmail':
            setListingAgentEmail(value);
            break;
          default:
            break;
    }
  };

  // Handler for save button
  const handleSave = () => {
    // Construct the amendmentData object from local state
    const updatedAmendmentData = {
      buyer1FirstName,
      buyer1LastName,
      buyer1Email,
      buyer2FirstName,
      buyer2LastName,
      buyer2Email,
      seller1Name, 
      seller2Name, 
      lastContractDate,
      listingAgentName,
      listingAgentEmail,
      contractCompletionDate,
      amendmentDate: amendmentDate.toISOString(), // Convert Date to string
      amendmentId,
      expirationDate,
      currentDates,
      newDates,
      otherDatesText,
      otherAmendmentsText,
      sendToSeller,
      packetDetails,
      propertyDescription,
      address,
      county,
      selectedContract,
      // Include other fields as necessary
    };
    dispatch(saveAmendmentData(formId, updatedAmendmentData));
    setIsEditMode(false);
  };

  const prevStep = () => {
    dispatch(setStep(step - 1)); // Dispatch action to update step in the Redux store
};

const nextStep = () => {
        handleSave(); // Save the data before proceeding
        dispatch(setStep(step + 1)); // Ensure setStep returns a plain object
}; 

useEffect(() => {
  if (formData?.data?.dateFields) {
    const dates = Object.entries(formData.data.dateFields).reduce((acc, [key, value]) => {
      // Check if the value is an object and has a date property
      if (typeof value === 'object' && value.hasOwnProperty('date')) {
        acc[key] = value.date; // Set key-value pair instead of pushing to an array
      }
      return acc;
    }, {});
    setCurrentDates(dates); // dates is now an object with key-value pairs
  }
}, [formData]);

  return (
        <>
        <div>
          <div className="max-h-[450px] overflow-y-auto w-[375px] md:w-[750px] px-4">
            <div className='inline-flex justify-between items-center align-center mb-6 w-full'>
                <div className='flex flex-col justify-start gap-2'>
                    <h1 className='text-[16px] md:text-lg text-black font-[Inter] justify-center'>Validate the following information:</h1>
                </div>
                <div className={`inline-flex ${isEditMode ? 'w-[150px] md:pr-4 pr-2' : 'w-auto' } justify-between`}>
                    {isEditMode && <button onClick={handleSave}>Save</button>}
                    {isEditMode && <button onClick={handleCancel}>Cancel</button>}
                    <button className={`text-shi-green font-[Inter] hover:text-slate-900 ${isEditMode ? 'hidden' : ''}`} onClick={handleEditClick} disabled={isEditMode}>Edit</button>
                </div>
            </div>
            
            <div className="grid grid-cols-2 mb-5 w-full gap-4">
                <div>
                    <div className='flex flex-col gap-2 mb-6'>
                        <p className='text-shi-green'>Contract</p>
                        <div className='flex flex-col justify-start'>
                                <span className="text-slate-600 text-sm pl-2 mb-2">Original Contract Signed: </span>
                                <span className='font-[Inter] font-semibold pl-2 text-[14px] md:text-[16px]'>{lastContractDate || "Date not available"}</span>
                        </div>
                    </div>

                    <div className='flex flex-col gap-2 mb-6'>
                        <p className='text-shi-green'>Buyers</p>
                        {isEditMode ?
                                <div className='flex flex-col justify-start gap-2'>
                                    <span className="text-slate-600 text-sm pl-2">Buyer 1 First Name: </span> 
                                    <input 
                                        type="text" 
                                        className="ml-4 py-1 px-3 h-[39px] rounded-md border border-slate-200 text-[14px] md:text-[16px]" 
                                        value={buyer1FirstName}
                                        onChange={(e) => handleInputChange('buyer1FirstName', e.target.value)}
                                    />
                                    <span className="text-slate-600 text-sm pl-2">Buyer 1 Last Name: </span> 
                                    <input 
                                        type="text" 
                                        className="ml-4 py-1 px-3 h-[39px] rounded-md border border-slate-200 text-[14px] md:text-[16px]" 
                                        value={buyer1LastName}
                                        onChange={(e) => handleInputChange('buyer1LastName', e.target.value)}
                                    />
                                    {/* <span className="text-slate-600 text-sm pl-2">Buyer 1 Email: </span> 
                                    <input 
                                        type="text" 
                                        className="ml-4 py-1 px-3 h-[39px] rounded-md border border-slate-200 text-[14px] md:text-[16px]" 
                                        value={buyer1Email}
                                        onChange={(e) => handleInputChange('buyer1Email', e.target.value)}
                                    /> */}
                                </div> :
                                <div className='flex flex-col justify-start pl-2'>
                                    <span className="text-slate-600 text-sm">Buyer 1: </span>
                                    <span className='font-semibold text-[14px] md:text-[16px]'>{`${buyer1FirstName}  ${buyer1LastName}`}</span>
                                    {/* <span className="text-slate-600 text-sm">Buyer 1 Email: </span>
                                    <span className='font-semibold text-[14px] md:text-[16px]'>{buyer1Email || "Buyer Info Not Available"}</span> */}
                                </div>
                            }
                            {formData?.data?.tenants.length > 0 &&
                                formData.data.tenants.map((tenant, index) => (
                                isEditMode ?
                                <div key={index} className="flex flex-col justify-start pl-2 gap-2">
                                    <span className="text-slate-600 text-sm">Buyer {index + 2} First Name: </span>
                                    <input 
                                        type="text" 
                                        className="py-1 px-3 h-[39px] rounded-md border border-slate-200 text-[14px] md:text-[16px]" 
                                        value={buyer2FirstName}
                                        onChange={(e) => handleInputChange('buyer2FirstName', e.target.value)}
                                    />
                                    <span className="text-slate-600 text-sm">Buyer {index + 2} Last Name: </span>
                                    <input 
                                        type="text" 
                                        className="py-1 px-3 h-[39px] rounded-md border border-slate-200 text-[14px] md:text-[16px]" 
                                        value={buyer2LastName}
                                        onChange={(e) => handleInputChange('buyer2LastName', e.target.value)}
                                    />
                                    {/* <span className="text-slate-600 text-sm">Buyer {index + 2} Email: </span>
                                    <input 
                                        type="text" 
                                        className="py-1 px-3 h-[39px] rounded-md border border-slate-200 text-[14px] md:text-[16px]" 
                                        value={buyer2Email}
                                        onChange={(e) => handleInputChange('buyer2Email', e.target.value)}
                                    /> */}
                                </div> :
                                <div key={index} className='flex flex-col justify-start pl-2'>                                    
                                    <span className="text-slate-600 text-[14px] md:text-[16px]">Buyer {index + 2}: </span>
                                    <strong>{`${buyer2FirstName} ${buyer2LastName}` || "Buyer Info Not Available"}</strong>
                                </div>
                            ))
                        }
                    </div>

                    <div className='flex flex-col gap-2 mb-6'>
                        <p className='text-shi-green'>Seller</p>
                        {isEditMode ?
                            <div className='flex flex-col justify-start pl-2 gap-2'>
                                <span className="text-slate-600 text-sm">Seller 1 Name: </span> 
                                <input 
                                    type="text" 
                                    className="py-1 px-3 h-[39px] rounded-md border border-slate-200 text-[14px] md:text-[16px]" 
                                    value={seller1Name}
                                    onChange={(e) => handleInputChange('seller1Name', e.target.value)}
                                />
                                <span className="text-slate-600 text-sm">Seller 2 Name: </span> 
                                <input 
                                    type="text" 
                                    className="py-1 px-3 h-[39px] rounded-md border border-slate-200 text-[14px] md:text-[16px]" 
                                    value={seller2Name}
                                    onChange={(e) => handleInputChange('seller2Name', e.target.value)}
                                />
                            </div> :
                            <div className='flex flex-col justify-start pl-2 gap-2'>
                                <div className='flex flex-col justify-start'>
                                    <span className="text-slate-600 text-sm">Seller 1 Name: </span>
                                    <span className='font-semibold text-[14px] md:text-[16px]'>{seller1Name || "Seller Info Not Available"}</span>
                                </div>
                                <div className='flex flex-col justify-start'>
                                    <span className="text-slate-600 text-sm">Seller 2 Name: </span>
                                    <span className='font-semibold text-[14px] md:text-[16px]'>{seller2Name || "Seller Info Not Available"}</span>
                                </div>
                            </div>
                        }
                    </div>
                </div>

                <div>
                  <div className='flex flex-col gap-2 mb-6'>
                    <p className=' text-shi-green'>Listing Agent</p>
                  {isEditMode ? (
                    <div>
                        <div className='flex flex-col gap-2'>
                            <div className='flex flex-col justify-start pl-2'>
                                <span className="text-slate-600 text-sm">Agent Name </span>
                                <input 
                                    type="text" 
                                    className="py-1 px-3 h-[39px] rounded-md border border-slate-200 text-[14px] md:text-[16px]" 
                                    value={listingAgentName}
                                    onChange={(e) => handleInputChange('listingAgentName', e.target.value)}
                                />
                            </div>
                            <div className="w-fulljustify-between gap-2 items-start flex flex-col mb-4">
                                <div className="relative flex flex-col mt-2 w-full border rounded-md border-slate-400 p-4">
                                    <span className="absolute -top-2 left-2 bg-white px-1 text-[12px] md:text-sm font-[Inter] text-slate-700"><b className='text-black'><i>Recommended</i></b></span>
                                    <div className='flex flex-col gap-1'>
                                        <span className='text-sm text-slate-600'>Listing Agent Email</span>
                                        <span className='text-[12px] font-bold text-shi-purple mb-2'>
                                            To send e-signature to the seller's, provide the listing agent email.
                                        </span>                        
                                    </div>
                                    <input 
                                        type="text" 
                                        className="py-1 px-3 h-[39px] rounded-md border border-slate-200" 
                                        value={listingAgentEmail}
                                        onChange={(e) => handleInputChange('listingAgentEmail', e.target.value)}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                  ) : (
                    <div className='flex flex-col gap-2'>
                        <div className='inline-flex items-center'>
                            <div className='flex flex-col justify-start pl-4'>
                                <span className="text-slate-600 text-sm">Agent Name: </span>
                                <span className='font-semibold text-[14px] md:text-[16px]'>{listingAgentName || "Agent Info Not Available"}</span>
                            </div>
                        </div>
                        <div className="w-fulljustify-between gap-2 items-start flex flex-col my-4">
                          <div className="relative flex flex-col mt-2 w-full border rounded-md border-slate-400 md:p-6 p-3">
                              <span className="absolute -top-2 left-2 bg-white px-1 text-[12px] md:text-sm font-[Inter] text-slate-700"><b className='text-black'><i>Recommended</i></b></span>
                              <div className='flex flex-col gap-1 w-full text-wrap'>
                                  <span className='text-sm text-slate-600'>Listing Agent Email</span>
                                  <span className='text-[12px] font-bold text-shi-purple mb-2'>
                                      To send e-signature to the seller's, provide the listing agent email.
                                  </span>                        
                              </div>
                                {/* <span className="text-slate-600 text-sm">Agent Email: </span> */}
                                <span className='font-semibold text-[14px] md:text-[16px] text-wrap break-words'>{listingAgentEmail || "Agent Info Not Available"}</span>
                            </div>
                        </div>
                    </div>
                  )}
                      </div>
                </div>
            </div>
          </div>
          <div className="flex w-full justify-center gap-10 md:gap-36 mt-2 bg-white">
              <button className={`w-[100px] md:w-[150px] justify-center text-center text-shi-green bg-white border-white text-[14px] md:text-lg font-normal font-['Inter'] p-1.5 rounded-md border hover:bg-slate-200 items-start flex ${isEditMode ? 'hidden' : ''}`} onClick={prevStep} disabled={isEditMode}>
                  Back
              </button>
              <button 
                  className={`w-[100px] md:w-[150px] justify-center text-center text-white bg-shi-green text-[14px] md:text-lg font-normal font-['Inter'] p-1.5 rounded-md border hover:bg-green-950 items-start flex ${isEditMode ? 'hidden' : ''}`}   
                  onClick={nextStep}
                  disabled={isEditMode}
              >Next
              </button>
          </div>    
        </div> 
    </>
  );
}

export default AmendDataValidation; 